import React from 'react';
import { projectQuad } from './shadeProjection';

/* One perspective-mapped roller shade panel drawn over glass: fabric + hem bar + shading, with
   day backlight (light-through for screen/filtering fabrics) or night interior ambience.
   Supports textureUrl (preferred) or hexColor (solid-color fallback).
   Rendering philosophy: the fabric layer preserves the original swatch colour as faithfully as
   possible. Room shadows and light-through effects are applied as separate, subtle layers so
   they do not substantially shift the perceived fabric hue.
   Blackout: the panel is 100% opaque (opaque backing under the texture, no light-through, no frame
   shadow on the fabric) — only a hairline light gap along the frame edges in daylight. */
const FOLD_COUNT = { hobbled: 8, cascade: 8, pleated: 14 };

/* Face of the lowered roman shade per fold style (CSS gradients over the fabric texture) */
const romanFace = (fold, foldPx, fullH) => {
  const p = (f) => Math.round(foldPx * f);
  const sides = 'linear-gradient(to right, rgba(0,0,0,0.08), rgba(0,0,0,0) 8%, rgba(0,0,0,0) 92%, rgba(0,0,0,0.08))';
  // Flat: smooth face, minimal lines — only a soft light falloff
  if (fold === 'flat') return `${sides}, linear-gradient(to bottom, rgba(255,255,255,0.06), rgba(0,0,0,0) 40%, rgba(0,0,0,0.06))`;
  if (fold === 'relaxed') return 'linear-gradient(to right, rgba(0,0,0,0.10), rgba(0,0,0,0) 14%, rgba(0,0,0,0) 86%, rgba(0,0,0,0.10)), linear-gradient(to bottom, rgba(0,0,0,0) 70%, rgba(0,0,0,0.12))';
  // Hobbled: soft, deep rounded loops all the way down
  if (fold === 'hobbled') return `${sides}, repeating-linear-gradient(to bottom, rgba(255,255,255,0.38) 0px, rgba(255,255,255,0.22) ${p(0.2)}px, rgba(255,255,255,0.04) ${p(0.45)}px, rgba(0,0,0,0.18) ${p(0.72)}px, rgba(0,0,0,0.42) ${p(0.9)}px, rgba(0,0,0,0.62) ${foldPx}px)`;
  // Pleated: crisp, narrow, hard-edged pleats from top to bottom
  if (fold === 'pleated') return `repeating-linear-gradient(to bottom, rgba(255,255,255,0.34) 0px, rgba(255,255,255,0.34) ${p(0.12)}px, rgba(255,255,255,0.08) ${p(0.14)}px, rgba(0,0,0,0.02) ${p(0.55)}px, rgba(0,0,0,0.30) ${p(0.96)}px, rgba(0,0,0,0.48) ${foldPx}px)`;
  // Cascade: subtle, thin, evenly spaced fold lines with slight depth
  if (fold === 'cascade') return `repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0px, rgba(0,0,0,0) ${foldPx - 4}px, rgba(255,255,255,0.22) ${foldPx - 3}px, rgba(0,0,0,0.10) ${foldPx - 2}px, rgba(0,0,0,0.34) ${foldPx - 1}px, rgba(0,0,0,0.12) ${foldPx}px)`;
  if (fold === 'aventura') {
    const gap = Math.max(18, Math.round(fullH / 10));
    return `repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0px, rgba(0,0,0,0) ${gap - 6}px, rgba(0,0,0,0.28) ${gap - 5}px, rgba(60,48,30,0.92) ${gap - 4}px, rgba(255,255,255,0.40) ${gap - 3}px, rgba(60,48,30,0.88) ${gap - 2}px, rgba(0,0,0,0.30) ${gap - 1}px, rgba(0,0,0,0) ${gap}px)`;
  }
  return `${sides}`;
};

export const ProjectedShade = ({ quad, cw, ch, textureUrl, hexColor, opacity = 1, mode = 'filtering', ambience = 'day', hemFrac = 0.035, railFrac = 0.025, system = 'roller', fold = 'flat', renderDrop = 1, testId, noHardware = false, body = false }) => {
  if (!cw || !ch) return null;
  const { w, h, transform } = projectQuad(quad, cw, ch);
  const base = {
    position: 'absolute', left: 0, top: 0, width: w, height: h,
    transform, transformOrigin: '0 0', backfaceVisibility: 'hidden', pointerEvents: 'none',
  };
  const t = Math.max(90, Math.round(w / 3));
  const pid = `fabric-tile-${testId}`;
  const blackout = mode === 'blackout';
  const exterior = ambience === 'exterior';   // seen from the street at night, interior lights on
  const night = ambience === 'night' || ambience === 'nightCity' || exterior;
  // Openness works the same day or night: the real night / Night City view shows through the weave
  // (night: the exterior is dark, so lights read through more strongly — keep the openness difference visible)
  const fabricOpacity = blackout || exterior ? 1 : night ? Math.max(0.55, 1 - (1 - opacity) * 1.8) : opacity;
  const glow = blackout ? 0 : mode === 'screen' ? Math.min(0.75, 0.45 + (1 - opacity) * 2.5) : 0.7;
  const through = blackout ? 0 : 1 - opacity;   // how much light passes through
  /* Wrapped hem pocket: hemFrac is relative to the CURRENT (dropped) panel so its on-screen size stays
     constant at every slider position; it lives inside the projected panel → follows the perspective. */
  const hemH = Math.max(5, Math.round(h * hemFrac));
  const railH = Math.max(6, Math.round(h * railFrac));

  /* Roman shade (bottom-up lift): as it is raised the bottom rises and the folds gather in a stack at the
     BOTTOM of the shade, under the flat fabric (stack ≈ 16% of the window when fully raised).
     Fold styles: flat (crisp creases), hobbled (looped cascading folds all the way down), relaxed (no creases,
     soft smile-curved bottom). */
  const roman = system === 'roman';
  const fullH = h / Math.max(0.05, renderDrop);
  const foldPx = Math.max(fold === 'cascade' ? 10 : 14, Math.round(fullH / (FOLD_COUNT[fold] || 6)));
  // Interlining adds body: a slightly thicker stack and rounder, fuller folds
  const stackH = roman ? Math.min(h, Math.max(hemH * (body ? 1.3 : 1), Math.round(fullH * (body ? 0.19 : 0.16) * (1 - renderDrop)))) : 0;
  const stackFold = Math.max(4, Math.round(stackH / Math.max(1, Math.round(6 * (1 - renderDrop)))));
  const sag = roman && fold === 'relaxed' ? Math.min(Math.round(w * 0.05), Math.round(h * 0.25)) : 0;
  if (sag) base.clipPath = `path('M0 0 H${w} V${h - sag} Q${w / 2} ${h + sag} 0 ${h - sag} Z')`;

  // DEBUG: bright outline to verify quad alignment against window frames
  const DEBUG_QUADS = false;

  return (
    <>
      {DEBUG_QUADS && <div style={{ ...base, zIndex: 99, border: '2px solid red', boxSizing: 'border-box', background: 'rgba(255,0,0,0.15)' }} />}
      {/* Layer 1 — Fabric: rendered at calibrated opacity. For blackout → fully opaque.
          No blend mode on the texture itself → swatch colour reproduced faithfully. */}
      <div style={{ ...base, zIndex: 5, opacity: fabricOpacity, filter: exterior ? (blackout ? 'brightness(0.45)' : `brightness(${(1 + glow * 0.6).toFixed(2)}) saturate(0.85)`) : undefined }} data-testid={testId} data-mode={mode} data-exterior={exterior ? 'true' : undefined}>
        {blackout && (
          /* Opaque backing: blocks the exterior completely even while the texture is still loading */
          <div style={{ position: 'absolute', inset: 0, backgroundColor: hexColor || '#8c8c8c' }} data-testid={`${testId}-blackout-backing`} />
        )}
        {textureUrl ? (
        <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block', position: 'relative' }}>
          <defs>
            <pattern id={pid} patternUnits="userSpaceOnUse" width={t * 2} height={t * 2}>
              <image href={textureUrl} width={t} height={t} preserveAspectRatio="none" />
              <image href={textureUrl} width={t} height={t} preserveAspectRatio="none" transform={`translate(${t * 2},0) scale(-1,1)`} />
              <image href={textureUrl} width={t} height={t} preserveAspectRatio="none" transform={`translate(0,${t * 2}) scale(1,-1)`} />
              <image href={textureUrl} width={t} height={t} preserveAspectRatio="none" transform={`translate(${t * 2},${t * 2}) scale(-1,-1)`} />
            </pattern>
          </defs>
          <rect width={w} height={h} fill={`url(#${pid})`} />
        </svg>
        ) : (
        /* Solid-colour fallback — normal blend so the hex reproduces accurately */
        <div style={{ position: 'relative', width: '100%', height: '100%', backgroundColor: hexColor || '#d4d4d4' }} />
        )}
        {/* Hem pocket — fabric wrapped around the weight bar: a fold seam at the top, a soft rounded
            body (light catches the top of the roll, shade underneath) and a thin edge with a drop shadow.
            The fabric texture underneath stays visible → reads as sewn fabric, not an exposed metal bar. */}
        {roman && (
          <>
            {/* Fabric between the headboard and the stack */}
            <div data-testid={`${testId}-roman-folds`} data-fold={fold} style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: stackH,
              background: romanFace(fold, foldPx, fullH),
            }} />
            {/* Stack of gathered folds at the bottom of the raised shade (bottom fold included) */}
            <div data-testid={`${testId}-roman-stack`} style={{ position: 'absolute', left: '-1%', right: '-1%', bottom: 0, height: stackH,
              background: stackH > hemH
                ? `repeating-linear-gradient(to bottom, rgba(255,255,255,${fold === 'relaxed' ? 0.18 : 0.32}) 0px, rgba(255,255,255,0.08) ${Math.round(stackFold * 0.3)}px, rgba(0,0,0,0.08) ${Math.round(stackFold * 0.65)}px, rgba(0,0,0,${fold === 'relaxed' ? 0.28 : 0.45}) ${stackFold}px)`
                : 'linear-gradient(to bottom, rgba(255,255,255,0.14), rgba(0,0,0,0.16))',
              boxShadow: body ? '0 -1px 3px rgba(0,0,0,0.22), 0 4px 9px rgba(0,0,0,0.36)' : '0 -1px 2px rgba(0,0,0,0.18), 0 3px 6px rgba(0,0,0,0.30)', borderRadius: body ? '0 0 6px 6px' : '0 0 3px 3px',
            }} />
            {/* Interlining body: padded side edges + a soft, slightly puffed face (fabric thickness, not a flat sheet) */}
            {body && <div data-testid={`${testId}-interlining-body`} style={{ position: 'absolute', inset: 0,
              background: 'linear-gradient(to right, rgba(0,0,0,0.16), rgba(0,0,0,0.04) 3%, rgba(255,255,255,0.05) 8%, rgba(0,0,0,0) 20%, rgba(0,0,0,0) 80%, rgba(255,255,255,0.05) 92%, rgba(0,0,0,0.04) 97%, rgba(0,0,0,0.16)), radial-gradient(ellipse 70% 55% at 50% 42%, rgba(255,255,255,0.07), rgba(255,255,255,0) 70%)',
              boxShadow: 'inset 0 0 6px rgba(0,0,0,0.12)',
            }} />}
          </>
        )}
        {!roman && !noHardware && <div data-testid={`${testId}-hem`} style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: hemH,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.16) 0, rgba(0,0,0,0.16) 1px, rgba(255,255,255,0.22) 1px, rgba(255,255,255,0.22) 2px, rgba(255,255,255,0.08) 30%, rgba(0,0,0,0.04) 60%, rgba(0,0,0,0.14) 100%)',
          boxShadow: '0 1px 0 rgba(0,0,0,0.18), 0 2px 5px rgba(0,0,0,0.28)',
        }} />}
        {/* Exposed fabric roller at top — the rolled fabric itself (texture shows through), shaded as a cylinder */}
        {!roman && !noHardware && <div data-testid={`${testId}-roller`} style={{ position: 'absolute', left: 0, right: 0, top: 0, height: railH,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.30) 0%, rgba(255,255,255,0.20) 30%, rgba(255,255,255,0.08) 50%, rgba(0,0,0,0.18) 85%, rgba(0,0,0,0.32) 100%)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.25)', borderRadius: '2px',
        }} />}
        {/* Side guides — thin tracks on left and right edges */}
        {!roman && !noHardware && <div style={{ position: 'absolute', left: 0, top: 0, bottom: hemH, width: Math.max(2, Math.round(w * 0.004)),
          background: 'linear-gradient(to right, rgba(60,60,60,0.6), rgba(80,80,80,0.3))',
        }} />}
        {!roman && !noHardware && <div style={{ position: 'absolute', right: 0, top: 0, bottom: hemH, width: Math.max(2, Math.round(w * 0.004)),
          background: 'linear-gradient(to left, rgba(60,60,60,0.6), rgba(80,80,80,0.3))',
        }} />}
      </div>

      {/* Layer 2 — Screen/filtering: subtle edge & top shadow for depth.
          Blackout: no shadow on the fabric; only a hairline daylight gap along the frame edges. */}
      {!blackout ? (
        <div
          style={{
            ...base, zIndex: 6,
            background: [
              'linear-gradient(to right, rgba(0,0,0,0.06), rgba(0,0,0,0) 12%, rgba(0,0,0,0) 88%, rgba(0,0,0,0.06))',
              'linear-gradient(to bottom, rgba(0,0,0,0.05), rgba(0,0,0,0) 12%)',
            ].join(','),
          }}
        />
      ) : !night && (
        <div
          data-testid={`${testId}-edge-light`}
          style={{
            ...base, zIndex: 6,
            background: [
              'linear-gradient(to right, rgba(255,255,255,0.55), rgba(255,255,255,0) 1.5%, rgba(255,255,255,0) 98.5%, rgba(255,255,255,0.55))',
              'linear-gradient(to bottom, rgba(255,255,255,0.45), rgba(255,255,255,0) 1.5%)',
            ].join(','),
          }}
        />
      )}

      {/* Layer 3 — Day light-through glow (screen & filtering only).
          A subtle, diffuse radial highlight that hints at exterior light
          without washing out the fabric colour. */}
      {!blackout && !night && through > 0.02 && (
        <div
          style={{
            ...base, zIndex: 7,
            background: `radial-gradient(ellipse 75% 65% at 50% 45%, rgba(255,255,255,${Math.min(0.30, through * 0.7).toFixed(3)}), rgba(255,255,255,0))`,
          }}
        />
      )}

      {/* Layer 4 — Night ambience: warm interior glow + optional dark exterior showing through.
          Night City: city lights glow through screen/filtering fabrics with a warm amber-blue tint. */}
      {/* Layer 5 — Street side (lights on): fabric back-lit by the warm room, brightest under the ceiling light;
          blackout stays dark with only a thin light leak at the edges */}
      {exterior && (blackout ? (
        <div style={{ ...base, zIndex: 7, boxShadow: 'inset 0 0 5px rgba(255,190,120,0.55)' }} data-testid={`${testId}-street-leak`} />
      ) : (
        <>
          <div style={{ ...base, zIndex: 7, background: `rgba(255,178,102,${(glow * 0.45).toFixed(3)})`, mixBlendMode: 'soft-light' }} />
          <div style={{ ...base, zIndex: 8, background: `radial-gradient(ellipse 70% 55% at 50% 30%, rgba(255,214,160,${(glow * 0.55).toFixed(3)}), rgba(255,170,90,${(glow * 0.12).toFixed(3)}) 70%, rgba(255,170,90,0))`, mixBlendMode: 'screen' }} data-testid={`${testId}-street-glow`} />
        </>
      ))}
      {night && !exterior && (
        <>
          <div style={{ ...base, zIndex: 7, background: 'rgba(255,176,96,0.10)', mixBlendMode: 'soft-light' }} />
        </>
      )}
    </>
  );
};

export default ProjectedShade;
